/**
 * Display catalog for HR Docket's subscription plans and one-time credit packs.
 * Mirrors the API's source of truth in `api/src/shared/lib/plans.ts` — keep the
 * slugs, prices, and limits in sync between the two. This module owns the
 * marketing copy (blurbs, feature bullets, the comparison table) that the API
 * doesn't need. See docs/pricing/IMPLEMENTATION.md.
 */

export type PlanSlug = "free" | "starter" | "growth" | "business";
export type CreditPackSlug = "extra-50-credits" | "extra-150-credits" | "extra-500-credits";

/**
 * Billing cadence for a subscription. A tier is one `PlanSlug`; the interval
 * selects between its monthly and yearly price. Yearly is priced at 10× monthly
 * (two months free). Mirrors `backend/src/lib/plans.ts`.
 */
export type BillingInterval = "month" | "year";

export type PlanDisplay = {
  slug: PlanSlug;
  name: string;
  /** Price in cents (USD); 0 for Free. */
  priceCents: number;
  /** Yearly price in cents; monthly × 10 (two months free). Paid tiers only. */
  priceCentsYearly?: number;
  /** One-line audience description. */
  bestFor: string;
  /** Max employee profiles across companies; -1 = unlimited. */
  employeeLimit: number;
  /** Max companies (workspaces) the account may own on this plan. */
  companyLimit: number;
  monthlyCredits: number;
  mostPopular: boolean;
  features: string[];
};

export type CreditPackDisplay = {
  slug: CreditPackSlug;
  name: string;
  priceCents: number;
  credits: number;
};

export const PLAN_ORDER: PlanSlug[] = ["free", "starter", "growth", "business"];

/**
 * Feature-level plan gating. Each key is a discrete feature the app locks below
 * a minimum plan tier; anyone on a lower plan sees it locked (with an upgrade
 * dialog) instead of being able to use it. Mirrors the API's `FEATURE_MIN_PLAN`
 * (api/src/shared/lib/plans.ts) — keep the two in sync.
 */
export type FeatureKey =
  | "alerts"
  | "customTemplates"
  | "leave"
  | "incidents"
  | "assets"
  | "broadcasts"
  | "policies"
  | "bulkGenerate"
  | "csvImport"
  | "complianceRisk"
  | "audit"
  | "auditExport"
  | "attendance"
  | "publicSigning"
  | "recurringReminders"
  | "credentials"
  | "credentialReminders"
  | "credentialRenewals"
  | "credentialReports"
  | "credentialReportPdf";

const MIN_PLAN: Record<FeatureKey, PlanSlug> = {
  alerts: "starter",
  customTemplates: "starter",
  leave: "free",
  incidents: "free",
  assets: "free",
  broadcasts: "starter",
  policies: "free",
  bulkGenerate: "starter",
  csvImport: "free",
  complianceRisk: "free",
  audit: "free",
  auditExport: "business",
  attendance: "free",
  publicSigning: "growth",
  recurringReminders: "starter",
  credentials: "free",
  credentialReminders: "starter",
  credentialRenewals: "growth",
  credentialReports: "growth",
  credentialReportPdf: "business",
};

/** The minimum plan a feature requires. */
export function featureMinPlan(feature: FeatureKey): PlanSlug {
  return MIN_PLAN[feature];
}

/**
 * Per-plan numeric quotas advertised on the pricing cards and enforced
 * server-side. `-1` = unlimited, `0` = not included. These are the defaults;
 * an admin can change any of them, and the API's numbers are the ones that
 * apply. Mirrors the API's `PLAN_LIMITS`.
 */
export type PlanQuotaKey =
  | "customTemplates"
  | "broadcastsPerCycle"
  | "publishedPolicies"
  | "attendanceSessionsPerCycle"
  | "employeeSigningPerCycle"
  | "publicSigningPerCycle"
  | "leaveEnrolledEmployees"
  | "assetItems"
  | "dailyEmails";

const LIMITS: Record<PlanSlug, Record<PlanQuotaKey, number>> = {
  free: q(1, 0, 1, 1, 3, 0, 3, 5, 50),
  starter: q(3, 5, 3, 10, 50, 0, 25, 45, 300),
  growth: q(7, 15, 15, 40, -1, 50, 60, 150, 1000),
  business: q(15, 40, -1, 100, -1, -1, -1, 500, 5000),
};

function q(
  customTemplates: number,
  broadcastsPerCycle: number,
  publishedPolicies: number,
  attendanceSessionsPerCycle: number,
  employeeSigningPerCycle: number,
  publicSigningPerCycle: number,
  leaveEnrolledEmployees: number,
  assetItems: number,
  dailyEmails: number,
): Record<PlanQuotaKey, number> {
  return {
    customTemplates,
    broadcastsPerCycle,
    publishedPolicies,
    attendanceSessionsPerCycle,
    employeeSigningPerCycle,
    publicSigningPerCycle,
    leaveEnrolledEmployees,
    assetItems,
    dailyEmails,
  };
}

/** A plan's default for one quota. */
export function planLimit(plan: PlanSlug, key: PlanQuotaKey): number {
  return LIMITS[plan][key];
}

/** Human-readable feature names, used for the upgrade-dialog copy. */
export const FEATURE_LABEL: Record<FeatureKey, string> = {
  alerts: "Expiration reminders",
  customTemplates: "The custom template builder",
  leave: "Leave management",
  incidents: "Incident logs",
  assets: "Asset & property tracking",
  broadcasts: "Company-wide broadcasts",
  policies: "Policy distribution",
  bulkGenerate: "Bulk document generation",
  csvImport: "CSV employee import",
  complianceRisk: "AI compliance & risk checks",
  audit: "Activity logs",
  auditExport: "Audit log export",
  attendance: "QR attendance tracking",
  publicSigning: "Signing without login",
  recurringReminders: "Repeating reminders",
  credentials: "Credential tracking",
  credentialReminders: "Credential expiry reminders",
  credentialRenewals: "Renewal uploads and approval",
  credentialReports: "Credential reports",
  credentialReportPdf: "Credential report PDF export",
};

/**
 * Whether an account on `plan` (null = still loading / no plan → treated as Free)
 * can use `feature`. A plan qualifies when its tier rank is at or above the
 * feature's minimum.
 */
export function canUseFeature(feature: FeatureKey, plan: PlanSlug | null): boolean {
  const planIndex = PLAN_ORDER.indexOf(plan ?? "free");
  return planIndex >= PLAN_ORDER.indexOf(featureMinPlan(feature));
}

// ── Plan cards ───────────────────────────────────────────────────────────────

type PlanCopy = {
  bestFor: string;
  employeeLimit: number;
  companyLimit: number;
  features: string[];
};

const COPY: Record<PlanSlug, PlanCopy> = {
  free: {
    bestFor: "Trying HR Docket, and micro teams",
    employeeLimit: 3,
    companyLimit: 1,
    features: [
      "Up to 3 employees, plus 3 samples to try things with",
      "10 AI credits a month",
      "50 MB of file storage",
      "Full AI HR document generator",
      "3 signature requests a month, to employees",
      "Leave, incidents, assets and attendance, with starter limits",
      "1 policy out for acknowledgement",
      "Employee self-service portal",
      "PDF downloads (watermarked)",
    ],
  },
  starter: {
    bestFor: "Small businesses",
    employeeLimit: 15,
    companyLimit: 1,
    features: [
      "Up to 15 employees",
      "120 AI credits a month",
      "1 GB of file storage",
      "Clean PDF downloads",
      "50 signature requests a month, to employees",
      "Bulk document generation",
      "Custom HR document builder (3 templates)",
      "Expiry reminders",
      "5 company broadcasts a month",
      "3 policies out for acknowledgement",
      "Up to 3 team members",
    ],
  },
  growth: {
    bestFor: "Growing teams with real compliance needs",
    employeeLimit: 50,
    companyLimit: 3,
    features: [
      "Everything in Starter",
      "Up to 50 employees",
      "250 AI credits a month",
      "5 GB of file storage",
      "Unlimited signature requests to employees",
      "50 signing links a month for anyone, no login",
      "15 policies out for acknowledgement",
      "15 company broadcasts a month",
      "7 custom templates",
      "Up to 7 team members",
      "Priority email support",
    ],
  },
  business: {
    bestFor: "Established and regulated organizations",
    employeeLimit: 140,
    companyLimit: 5,
    features: [
      "Everything in Growth",
      "140 employees, then $1 per employee a month",
      "600 AI credits a month, +10 per extra employee",
      "10 GB of file storage, +50 MB per extra employee",
      "Unlimited signing, including without login",
      "Unlimited policies and cases",
      "Up to 5 companies",
      "Audit log export and 3-year activity history",
      "15 custom templates, with tuning",
      "A custom generator tool built for you",
      "Unlimited team members",
      "Dedicated support",
    ],
  },
};

type PlanPrices = Omit<PlanDisplay, keyof PlanCopy>;

const PRICES: Record<PlanSlug, PlanPrices> = {
  free: { slug: "free", name: "Free", priceCents: 0, monthlyCredits: 10, mostPopular: false },
  starter: {
    slug: "starter",
    name: "Starter",
    priceCents: 1900,
    priceCentsYearly: 19000,
    monthlyCredits: 120,
    mostPopular: false,
  },
  growth: {
    slug: "growth",
    name: "Growth",
    priceCents: 4900,
    priceCentsYearly: 49000,
    monthlyCredits: 250,
    mostPopular: true,
  },
  business: {
    slug: "business",
    name: "Business",
    priceCents: 9900,
    priceCentsYearly: 99000,
    monthlyCredits: 600,
    mostPopular: false,
  },
};

export const PLANS: Record<PlanSlug, PlanDisplay> = {
  free: { ...PRICES.free, ...COPY.free },
  starter: { ...PRICES.starter, ...COPY.starter },
  growth: { ...PRICES.growth, ...COPY.growth },
  business: { ...PRICES.business, ...COPY.business },
};

// ── Comparison table ────────────────────────────────────────────

/** One cell: a tick, a dash, or a short value such as "3" or "Credits". */
export type ComparisonCell = boolean | string;

export type ComparisonRow = {
  label: string;
  /** Not built yet; shown with a "Soon" badge. */
  soon?: boolean;
  values: [ComparisonCell, ComparisonCell, ComparisonCell, ComparisonCell];
};

export type ComparisonSection = { title: string; rows: ComparisonRow[] };

/** The full plan comparison from docs/pricing/PRICING-V2.md, Free → Business. */
export const COMPARISON: ComparisonSection[] = [
  {
    title: "Basics",
    rows: [
      { label: "Employees", values: ["3 + 3 samples", "15", "50", "140, then $1 each"] },
      { label: "AI credits a month", values: ["10", "120", "250", "600"] },
      { label: "File storage", values: ["50 MB", "1 GB", "5 GB", "10 GB"] },
      { label: "PDF downloads", values: ["Watermarked", "Clean", "Clean", "Clean"] },
      { label: "Team members", values: ["1", "3", "7", "Unlimited"] },
      { label: "Companies", values: ["1", "1", "3", "5"] },
    ],
  },
  {
    title: "Documents",
    rows: [
      { label: "AI HR document generator", values: [true, true, true, true] },
      {
        label: "AI-assisted drafting and risk checks",
        values: ["Credits", "Credits", "Credits", "Credits"],
      },
      { label: "Custom templates", values: ["1", "3", "7", "15"] },
      { label: "Custom HR document builder", values: [false, true, true, "With tuning"] },
      { label: "Bulk document generation", values: [false, true, true, true] },
      { label: "Custom generator tool built for you", values: [false, false, false, "1"] },
    ],
  },
  {
    title: "Signatures",
    rows: [
      {
        label: "Signature requests to employees",
        values: ["3 a month", "50 a month", "Unlimited", "Unlimited"],
      },
      { label: "Signing without login", values: [false, false, "50 a month", "Unlimited"] },
    ],
  },
  {
    title: "Employee records",
    rows: [
      { label: "Employee profiles and CSV import", values: [true, true, true, true] },
      { label: "Employee self-service portal", values: [true, true, true, true] },
      { label: "Employee folders", soon: true, values: ["3", "15", "40", "Unlimited"] },
      { label: "Business folders", soon: true, values: [false, "3", "10", "Unlimited"] },
      { label: "Personal notes", soon: true, values: [false, true, true, true] },
    ],
  },
  {
    title: "Reminders",
    rows: [
      { label: "Expiry reminders", values: [false, true, true, true] },
      {
        label: "Recurring reminders",
        soon: true,
        values: [false, "25 active", "Unlimited", "Unlimited"],
      },
    ],
  },
  {
    title: "Policies",
    rows: [
      { label: "Policies out for acknowledgement", values: ["1", "3", "15", "Unlimited"] },
      {
        label: "Policy quizzes",
        soon: true,
        values: [false, "Manual", "Manual + AI", "Manual + AI"],
      },
      { label: "Rollout dashboard and escalation", soon: true, values: [false, false, true, true] },
      { label: "Hard block until acknowledged", soon: true, values: [false, false, false, true] },
    ],
  },
  {
    title: "Daily operations",
    rows: [
      { label: "Employees in leave tracking", values: ["3", "25", "60", "Unlimited"] },
      { label: "Asset items", values: ["5", "45", "150", "500"] },
      { label: "Company broadcasts a month", values: [false, "5", "15", "40"] },
      { label: "Attendance sessions a month", values: ["1", "10", "40", "100"] },
      { label: "Employee requests", soon: true, values: [false, true, true, true] },
    ],
  },
  {
    title: "Investigations",
    rows: [
      { label: "Incident log", values: [true, true, true, true] },
      {
        label: "Case management (open at once)",
        soon: true,
        values: [false, "2", "10", "Unlimited"],
      },
      { label: "Witness links", soon: true, values: [false, false, true, true] },
      { label: "Anonymous reporting channel", soon: true, values: [false, false, false, true] },
    ],
  },
  {
    title: "Admin and support",
    rows: [
      { label: "Activity log kept for", values: ["15 days", "2 months", "1 year", "3 years"] },
      { label: "Audit log export", values: [false, false, false, true] },
      { label: "Support", values: ["Help center", "Email", "Priority email", "Dedicated"] },
    ],
  },
];

export const CREDIT_PACK_ORDER: CreditPackSlug[] = [
  "extra-50-credits",
  "extra-150-credits",
  "extra-500-credits",
];

export const CREDIT_PACKS: Record<CreditPackSlug, CreditPackDisplay> = {
  "extra-50-credits": {
    slug: "extra-50-credits",
    name: "Extra AI Credits (50)",
    priceCents: 900,
    credits: 50,
  },
  "extra-150-credits": {
    slug: "extra-150-credits",
    name: "Extra AI Credits (150)",
    priceCents: 1900,
    credits: 150,
  },
  "extra-500-credits": {
    slug: "extra-500-credits",
    name: "Extra AI Credits (500)",
    priceCents: 4900,
    credits: 500,
  },
};

/** "$0" for free, "$19" for whole-dollar, "$19.99" otherwise. */
export function formatPlanPrice(priceCents: number): string {
  if (priceCents === 0) return "$0";
  const dollars = priceCents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/**
 * The effective monthly price of a yearly plan (yearly total ÷ 12), formatted
 * like `formatPlanPrice`. E.g. Starter's $190/yr → "$15.83".
 */
export function formatEffectiveMonthly(priceCentsYearly: number): string {
  return formatPlanPrice(Math.round(priceCentsYearly / 12));
}

/**
 * The headline price to show for a plan at a given interval: the monthly price
 * for "month", or the effective monthly price (billed annually) for "year".
 * Free stays "$0". Yearly falls back to the monthly price when no yearly price
 * is configured for the tier.
 */
export function planHeadlinePrice(plan: PlanDisplay, interval: BillingInterval): string {
  if (plan.priceCents === 0) return "$0";
  if (interval === "year" && plan.priceCentsYearly) {
    return formatEffectiveMonthly(plan.priceCentsYearly);
  }
  return formatPlanPrice(plan.priceCents);
}

/** Whether any paid tier has a yearly price configured (drives the toggle). */
export const HAS_YEARLY_PLANS: boolean = PLAN_ORDER.some(
  (slug) => (PLANS[slug].priceCentsYearly ?? 0) > 0,
);

/**
 * The checkout slug for a (tier, interval) pair — passed to
 * `authClient.checkout`. Monthly uses the bare slug; yearly appends `-yearly`.
 * Must stay in step with the `-yearly` SKU naming in `backend/src/lib/plans.ts`.
 */
export function checkoutSlugForInterval(slug: string, interval: BillingInterval): string {
  return interval === "year" ? `${slug}-yearly` : slug;
}
